/**
 * iReal Pro export (.irealb URL format).
 *
 * iReal Pro uses a proprietary URL scheme: irealb://...
 * Format reference: https://irealpro.com/ireal-pro-file-format/
 *
 * We generate a simplified single-key chart suitable for import.
 * iReal Pro accepts: irealb://SongTitle=Artist=Style=Key=q=Measures=n=0=1=0
 *
 * Chord encoding: each measure is wrapped in [ ], cells are 1-4 chords
 * separated by spaces. Section markers use s (start) and variants.
 *
 * This implementation generates a best-effort chart from our alignment data.
 */

import { transposeChord } from './chordTranspose';

const STYLE_DEFAULT = 'Medium Swing';

/**
 * Convert a chord name to iReal Pro notation (minor → -, major7 → Δ, etc.)
 */
function toIRealChord(chord) {
  if (!chord) return 'x'; // no chord / repeat
  return chord
    .replace(/maj7/gi, 'Δ')
    .replace(/maj/gi, 'Δ')
    .replace(/min7/gi, '-7')
    .replace(/min/gi, '-')
    .replace(/m7/g, '-7')
    .replace(/m(?=[^a]|$)/g, '-')
    .replace(/add9/gi, '2')
    .replace(/sus2/gi, 'sus2')
    .replace(/sus4/gi, 'sus')
    .replace(/sus/gi, 'sus')
    .replace(/dim7/gi, 'o7')
    .replace(/dim/gi, 'o')
    .replace(/aug/gi, '+')
    .replace(/\(/g, '')
    .replace(/\)/g, '');
}

/**
 * Build an iReal Pro URL from chord alignment data.
 *
 * @param {string} title
 * @param {string} songKey  e.g. "A major" | "F# minor"
 * @param {Array}  alignment  [{line, chord|null}] flat chord list
 * @param {string} [artist]
 * @param {number} [transposition]  applied semitone offset
 * @param {number} [capo]
 */
export function exportToIReal({
  title,
  songKey,
  alignment,
  artist = '',
  transposition = 0,
  capo = 0,
}) {
  if (!alignment || alignment.length === 0) return null;

  // Extract unique chords across all lines
  const chordSequence = [];
  const sectionMarkers = [];

  alignment.forEach((lineObj, li) => {
    const text = lineObj.line || '';
    const isSection = /^\[.+\]$/.test(text.trim());
    const chords = lineObj.chords || {};

    if (isSection) {
      sectionMarkers.push({ index: chordSequence.length, name: text.trim().replace(/[[\]]/g, '') });
      return;
    }

    // Collect chords for this line (in word order)
    const wordChords = Object.entries(chords)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, c]) => c)
      .filter(Boolean);

    if (wordChords.length === 0) return;

    wordChords.forEach(chord => {
      let c = chord;
      if (transposition) c = transposeChord(c, transposition, null);
      if (capo) c = transposeChord(c, capo, null);
      chordSequence.push(toIRealChord(c));
    });
  });

  if (chordSequence.length === 0) return null;

  // Group chords into measures of 4 beats (simplified: one chord per beat placeholder)
  // For a simple chart, pack up to 4 chords per measure
  const measures = [];
  for (let i = 0; i < chordSequence.length; i += 4) {
    measures.push(chordSequence.slice(i, i + 4).join(' '));
  }

  // Build iReal Pro measure notation: [chord1 chord2 chord3 chord4 ]
  const body = measures.map(m => `[${m} ]`).join('');

  // Key format: "A" for major, "A-" for minor
  const keyParts = (songKey || 'C major').split(' ');
  const keyRoot = keyParts[0] || 'C';
  const keyMode = (keyParts[1] || 'major').toLowerCase();
  const iRealKey = keyRoot + (keyMode === 'minor' ? '-' : '');

  // URL encode pieces
  const encode = s => encodeURIComponent(s.replace(/=/g, ' '));
  const url = `irealb://${encode(title || 'Untitled')}=${encode(artist)}=${encode(STYLE_DEFAULT)}=${encode(iRealKey)}=1=${encode(body)}=n=0=1=0`;
  return url;
}

/**
 * Trigger a download of a .irealb file.
 */
export function downloadIReal(songData) {
  const url = exportToIReal(songData);
  if (!url) return;

  // iReal Pro files are just text files containing the URL
  const blob = new Blob([url], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${(songData.title || 'song').replace(/[^a-z0-9 _-]/gi, '_')}.irealb`;
  a.click();
  URL.revokeObjectURL(a.href);
}
